import type { RssItem } from '../lib/api.js';
import type { DashboardResource } from '../lib/dashboardResource.js';
import CommandPanel from './ui/CommandPanel.js';
import { usePersistedPanelState } from '../hooks/usePersistedPanelState.js';
import { useId, type ReactNode } from 'react';
import LiveBadge from './ui/LiveBadge.js';
import Skeleton from './ui/Skeleton.js';
import SectionHeader from './ui/SectionHeader.js';

// RssCarousel v0.2.2 — collapsible persisted panel.
// Cards are fixed-height (110px) so the carousel has a uniform shape regardless
// of item title length, with truncation handled by line-clamp. Provider name
// sits in a separator-bordered header and the title fills the body. Empty and
// loading states render the same chrome as the populated state so the page
// layout doesn't jump as data arrives.

// Compact relative-age formatter. Stops at days; older items shouldn't reach
// the carousel since the aggregator caps history at ~24h.
function fmtAge(iso: string): string {
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
}

interface Props {
  resource: DashboardResource<RssItem[]>;
}

export default function RssCarousel({ resource }: Props) {
  const [expanded, setExpanded] = usePersistedPanelState('panel:recent-updates', true);
  const contentId = useId();
  const { data, loading, ready, stale, error } = resource;
  const items = data ?? [];
  const statusText = error ? 'feed unavailable' : loading ? 'updating' : stale ? 'stale' : null;
  const statusMode = error ? 'error' : loading ? 'updating' : stale ? 'stale' : 'ok';
  const itemCount = items.length;
  let body: ReactNode;
  const itemCountBadge = itemCount === 1 ? '1 item' : `${itemCount} items`;
  const right = statusText ? (
    <span className="flex items-center gap-2">
      <span>{itemCountBadge}</span>
      <LiveBadge mode={statusMode}>{statusText}</LiveBadge>
    </span>
  ) : (
    <span className="text-xs">{itemCountBadge}</span>
  );

  if (!ready && loading && items.length === 0) {
    body = (
      <div className="px-3 py-6 text-center text-dim text-xs font-mono">
        <Skeleton className="mx-auto h-5 w-24" />
        <div className="mt-2">loading…</div>
      </div>
    );
  } else if (error && items.length === 0) {
    body = (
      <div className="px-3 py-6 text-xs text-bad font-mono text-center">
        failed to load feed{error ? `: ${error}` : ''}
      </div>
    );
  } else if (items.length === 0) {
    body = (
      <div className="px-3 py-6 text-xs text-dim font-mono text-center">
        feed is warming up — no items yet
      </div>
    );
  } else {
    body = (
      <div
        className="flex gap-3 overflow-x-auto px-3 py-3 snap-x snap-mandatory scroll-px-3"
        style={{ scrollbarWidth: 'thin' }}
      >
        {items.map((it) => (
          <a
            key={`${it.provider_slug}-${it.link}`}
            href={it.link}
            target="_blank"
            rel="noopener noreferrer"
            title={it.title}
            className="snap-start shrink-0 w-[300px] sm:w-[340px] h-[120px] flex flex-col rounded border border-border hover:border-accent bg-surface2/40 hover:bg-surface2 transition-colors text-xs font-mono"
          >
            <div className="px-3 pt-2 pb-1 flex items-center justify-between gap-2 border-b border-border/50">
              <span className="text-accent text-[10px] tracking-[1.5px] uppercase truncate">
                {it.provider_name}
              </span>
              <span className="text-dim text-[10px] shrink-0">{fmtAge(it.published_at)} ago</span>
            </div>
            <div className="px-3 py-2 flex-1 overflow-hidden">
              <p className="text-text text-[12px] leading-snug overflow-hidden line-clamp-3">
                {it.title}
              </p>
            </div>
          </a>
        ))}
      </div>
    );
  }

  return (
    <CommandPanel bodyClassName="min-w-0 overflow-hidden">
      <SectionHeader
        title="RECENT UPDATES · TIER 0"
        right={right}
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        contentId={contentId}
      />
      {expanded && body}
    </CommandPanel>
  );
}
